import type { CVData } from "@/lib/cv-templates";
import { analyseCvText, type AtsAnalysis } from "@/lib/ats-engine";

export type AtsCheck = {
  id: string;
  label: string;
  passed: boolean;
  weight: number;
  detail?: string;
};

export type AtsReport = {
  score: number; // 0-100
  band: "poor" | "fair" | "good" | "excellent";
  checks: AtsCheck[];
  /** Keywords from the target role / job spec missing from the CV. */
  missingKeywords: string[];
  /** Highest-leverage fixes, ordered by score impact. */
  priorityFixes: { label: string; fix: string; points: number }[];
};

/**
 * Renders structured CV data into the exact ATS-safe plain text layout the
 * scanner expects, so the builder and the scanner read the same document.
 */
export function cvDataToAtsText(cv: CVData): string {
  const L: string[] = [];
  const push = (s?: string) => {
    if (s && s.trim()) L.push(s.trim());
  };

  push(cv.fullName);
  push(cv.headline);
  push(cv.location);
  push(cv.phone);
  push(cv.email);
  for (const l of cv.links || []) push(l.url || l.label);

  if (cv.summary?.trim()) {
    L.push("", "PROFESSIONAL SUMMARY", cv.summary.trim());
  }

  if ((cv.skills || []).length) {
    L.push("", "CORE SKILLS", cv.skills.join(", "));
  }

  if ((cv.experience || []).length) {
    L.push("", "EXPERIENCE");
    for (const e of cv.experience) {
      const head = [e.role, e.company].filter(Boolean).join(" — ");
      push(e.location ? `${head}, ${e.location}` : head);
      const dates = [e.start, e.end].filter(Boolean).join(" – ");
      push(dates);
      for (const b of e.bullets || []) push(`• ${b}`);
      L.push("");
    }
  }

  if ((cv.education || []).length) {
    L.push("", "EDUCATION");
    for (const ed of cv.education) {
      push(
        [ed.qualification, ed.institution, ed.year].filter(Boolean).join(" — "),
      );
      push(ed.notes);
    }
  }

  if ((cv.certifications || []).length) {
    L.push("", "CERTIFICATIONS");
    for (const c of cv.certifications!)
      push([c.name, c.issuer, c.year].filter(Boolean).join(" — "));
  }

  if ((cv.achievements || []).length) {
    L.push("", "ACHIEVEMENTS");
    for (const a of cv.achievements!) push(`• ${a}`);
  }

  if ((cv.awards || []).length) {
    L.push("", "ACHIEVEMENTS");
    for (const a of cv.awards!)
      push(`• ${[a.title, a.issuer, a.year, a.detail].filter(Boolean).join(" — ")}`);
  }

  if ((cv.languages || []).length) {
    L.push("", "LANGUAGES", cv.languages!.join(", "));
  }

  L.push("", "REFERENCES", cv.references?.trim() || "References available on request.");

  return L.join("\n");
}

/**
 * Computes an ATS readiness score for a generated CV.
 *
 * Single source of truth: this delegates to the same `analyseCvText` engine
 * used by the ATS scanner and the AI revamp pipeline, so a CV built here and
 * the same CV pasted into the scanner always produce an identical score.
 */
export function computeAtsReport(
  cv: CVData | null,
  targetRole: string,
  jobSpec = "",
): AtsReport {
  if (!cv) {
    return { score: 0, band: "poor", checks: [], missingKeywords: [], priorityFixes: [] };
  }

  const analysis: AtsAnalysis = analyseCvText(
    cvDataToAtsText(cv),
    targetRole || "",
    jobSpec,
  );

  const checks: AtsCheck[] = analysis.checks.map((c) => ({
    id: c.id,
    label: c.label,
    passed: c.passed,
    weight: c.weight,
    detail: c.passed ? c.detail : c.fix || c.detail,
  }));

  return {
    score: analysis.score,
    band: analysis.band,
    checks,
    missingKeywords: analysis.missingKeywords,
    priorityFixes: analysis.priorityFixes,
  };
}
